import { formatZi } from '@/domains/tickets/utils/tier-state';
import type { WasteContainerRequestDto, FelRecipient } from '../api/waste-container-requests.api';

/**
 * Starea fiecarui fel de recipient pe ecranul profesorului (SP-04) — partea PURA.
 *
 * `vitest.config.ts` ruleaza pe `environment: 'node'`, fara jsdom, deci
 * componentele nu se pot monta: tot ce se poate verifica automat traieste aici.
 *
 * 🔴 REGULA CARE DA FORMA FISIERULUI: starea se citeste INAINTE de apasare.
 * Backendul refuza a doua cerere pe acelasi fel cu 409 («Școala a fost deja
 * dotată»), dar mesajul ala e o plasa, nu fluxul — profesorul n-are voie sa
 * ajunga la el. Un ecran care te lasa sa apesi si apoi te refuza te invata ca
 * aplicatia nu stie ce contine. De-aia felul deja cerut **nu are buton**, iar
 * decizia „are buton sau nu" se ia aici, pe date, nu in randare.
 *
 * ⚠️ Timestamp-urile sunt **string-uri ISO**, desi tipul generat spune `Date`:
 * `transformers.gen.ts` nu ruleaza niciodata. De-aia functiile de aici primesc
 * `unknown` si verifica la rulare, in loc sa se increada in tip.
 */

/** Ce vede profesorul pentru UN fel de recipient. */
export type StareFel =
    | { stare: 'disponibil' }
    /** A cerut, si nimeni nu s-a ocupat inca (`NOUA`). */
    | { stare: 'ceruta'; dataCererii: string | null }
    /**
     * Un OM de la ECOTIC a luat cererea in lucru (`PROCESATA`).
     *
     * 🔴 Cuvantul e «preluată», si nu din stil. Javadoc-ul enumului spune ca
     * `PROCESATA` inseamna «un ADMIN sau DBEST a confirmat ca **se ocupa** de
     * trimitere» — nu „s-a trimis", nu „s-a livrat", nu „ai primit". «Trimisă» ar
     * revendica o expediere pe care n-o stim; «Aprobată» ar sugera ca putea fi
     * respinsa, si nu exista flux de respingere.
     */
    | { stare: 'preluata'; dataPreluarii: string | null; dataCererii: string | null }
    /**
     * Recipientele au ajuns la scoala, confirmat de un OM (`LIVRATA`, SE-18).
     *
     * Spre deosebire de `preluata`, aici «livrat» chiar e adevarat — nu mai e o
     * promisiune. Controlul negativ din teste ramane valabil pentru `ceruta`/
     * `preluata` (unde livrarea NU s-a intamplat inca), dar nu se mai aplica aici.
     */
    | { stare: 'livrata'; dataLivrarii: string | null; dataPreluarii: string | null; dataCererii: string | null };

/** Un ISO valid, formatat romaneste; orice altceva ⇒ `null`, si randul se scurteaza. */
function ziSauNimic(valoare: unknown): string | null {
    return typeof valoare === 'string' ? formatZi(valoare) : null;
}

/**
 * Starea unui fel, din cererile scolii.
 *
 * 🔴 **`trimisLaDt` NU se citeste aici, si nu e o omisiune.** E instalatia
 * noastra: spune daca aplicatia a dispecerizat un email catre logistica. Daca a
 * plecat sau nu e problema noastra, nu a profesorului — iar pe ecran ar deveni un
 * traseu promis. Regula e a lui S1 si e verificata de un test dedicat, cu doua
 * fixturi: una cu campul gol, una cu el setat.
 *
 * ⚠️ Ordinea conteaza si e pinuita: **`LIVRATA` bate `PROCESATA` bate `NOUA`**. Regula de
 * business e o cerere per scoala/editie/fel, deci in practica exista una singura;
 * dar functia nu are de unde sti asta, si un raspuns nedeterminist ar fi mai rau
 * decat o regula scrisa. Cea mai avansata stare castiga.
 */
export function stareFel(
    cereri: readonly WasteContainerRequestDto[] | undefined,
    fel: FelRecipient
): StareFel {
    const aleFelului = (cereri ?? []).filter((c) => c?.fel === fel);
    if (aleFelului.length === 0) return { stare: 'disponibil' };

    const livrata = aleFelului.find((c) => c.stare === 'LIVRATA');
    if (livrata) {
        return {
            stare: 'livrata',
            dataLivrarii: ziSauNimic(livrata.livrataLaDt),
            dataPreluarii: ziSauNimic(livrata.procesataLaDt),
            dataCererii: ziSauNimic(livrata.timestamp),
        };
    }

    const preluata = aleFelului.find((c) => c.stare === 'PROCESATA');
    if (preluata) {
        return {
            stare: 'preluata',
            dataPreluarii: ziSauNimic(preluata.procesataLaDt),
            dataCererii: ziSauNimic(preluata.timestamp),
        };
    }

    return { stare: 'ceruta', dataCererii: ziSauNimic(aleFelului[0].timestamp) };
}

/**
 * Numele felului, asa cum il vede omul — cu bucata de cantitate doar cand exista.
 *
 * `cantitate` are sens doar la minirecipiente si e optionala in DTO, deci
 * absenta ei nu e o eroare: randul se scurteaza. **Nu se scrie „× 0"** si nu se
 * inventeaza un numar — aceeasi regula ca la marimea fisierului din SP-03.
 *
 * ⚠️ Nu e „kilograme". Ecranul asta CERE echipament, nu preda deseuri — vezi
 * comentariul din pagina despre cele patru intrari de meniu cu „comandă".
 */
export function formateazaFel(fel: FelRecipient, cantitate?: number | null): string {
    const nume = fel === 'RECIPIENT_DBA' ? 'Recipient DBA' : 'Minirecipiente DBA';
    const n = typeof cantitate === 'number' && Number.isFinite(cantitate) && cantitate > 0
        ? cantitate
        : null;
    return n === null ? nume : `${nume} × ${n}`;
}

/** Minirecipientele se livreaza in baxuri de 100 (ECOTIC, 2026-10-16). */
export const PAS_MINIRECIPIENTE = 100;

/** Textul ECOTIC (2026-10-16), verbatim — si mesajul backendului la refuz. Nu-l reformula. */
export const INFO_CANTITATE_MINIRECIPIENTE =
    'Comanda minimă este pentru 100 buc. minirecipiente. Pentru comenzile care depășesc această cantitate, numărul de bucăți selectat trebuie să fie multiplu de 100 (200/300/400, etc.).';

/**
 * Cantitatea tastata de profesor: minim 100, apoi multiplu de 100. Primeste textul din input,
 * nu un numar — `Number('')` e 0 si `Number('1e2')` e 100, deci se cer explicit doar cifre.
 *
 * Aceeasi regula o aplica backendul la creare (`CANTITATE_INVALIDA`); corectarea facuta de admin
 * din «Comenzi recipiente» ramane libera, deliberat (owner, 2026-10-16).
 */
export function cantitateMinirecipienteValida(valoare: string): boolean {
    const text = valoare.trim();
    if (!/^\d+$/.test(text)) return false;
    const n = Number(text);
    return n >= PAS_MINIRECIPIENTE && n % PAS_MINIRECIPIENTE === 0;
}

/**
 * Adresa la care se cer recipientele, din datele scolii.
 *
 * Scoala are DOUA adrese si un comutator intre ele
 * (`adresaLivrareAceeasiCuScoala`), pentru ca livrarea nu se face intotdeauna la
 * sediu. Alegerea e aceeasi pe care o face formularul de predare a deseurilor;
 * daca ar fi facuta altfel aici, aceeasi scoala ar avea doua „adrese de livrare"
 * in acelasi produs, si nimeni n-ar sti care e cea buna.
 *
 * ⚠️ Trece prin `sanitizeAddress` in pagina, nu aici: backendul intoarce `null`
 * pe campurile optionale, iar schemele Zod ale produsului resping `null` (nu
 * doar `undefined`) — a blocat deja 97,5 % din scoli sa-si salveze datele.
 */
export function adresaLivrareScoala<T>(scoala: {
    adresa?: T;
    adresaLivrare?: T;
    adresaLivrareAceeasiCuScoala?: boolean;
} | undefined | null): T | undefined {
    if (!scoala) return undefined;
    if (scoala.adresaLivrareAceeasiCuScoala === false && scoala.adresaLivrare) {
        return scoala.adresaLivrare;
    }
    return scoala.adresa ?? scoala.adresaLivrare;
}

/**
 * Telefonul precompletat pentru livrare: al utilizatorului logat, iar daca profilul nu are unul
 * (null, sir gol sau doar spatii), al scolii.
 *
 * `||`, nu `??`: backendul poate intoarce `""` pe profil, iar cu `??` un sir gol ar fi castigat si
 * campul ar fi ramas gol, desi scoala avea telefon.
 */
export function telefonContactImplicit(
    telefonUtilizator: string | null | undefined,
    telefonScoala: string | null | undefined
): string {
    return telefonUtilizator?.trim() || telefonScoala?.trim() || '';
}

/**
 * Randul de stare, gata de randat — sau `null` cand felul e inca disponibil.
 *
 * Textele stau AICI, nu in componenta, tocmai ca sa fie acoperite de teste: sunt
 * chiar lucrul pe care S1 il verifica („nu contine «trimis», «livrat»,
 * «aprobat»"), si o poarta pe ele e singura care ramane dupa ce plecam.
 */
export function textStare(stare: StareFel, fel: FelRecipient): string | null {
    // Forma articulata, scrisa pe fel, nu obtinuta prin `toLowerCase()` din nume:
    // „DBA" e un acronim, iar „ați comandat recipient dba" arata ca o scapare de robot.
    const articulat = fel === 'RECIPIENT_DBA' ? 'recipientul DBA' : 'minirecipientele DBA';
    if (stare.stare === 'disponibil') return null;
    if (stare.stare === 'ceruta') {
        return stare.dataCererii
            ? `Ați comandat ${articulat} pe ${stare.dataCererii}.`
            : `Ați comandat ${articulat}.`;
    }
    if (stare.stare === 'livrata') {
        return stare.dataLivrarii
            ? `Comanda a fost livrată pe ${stare.dataLivrarii}.`
            : 'Comanda a fost livrată.';
    }
    return stare.dataPreluarii
        ? `Comanda a fost preluată pe ${stare.dataPreluarii}.`
        : 'Comanda a fost preluată.';
}
